import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import React from "react";

import { SortableItemWrapper } from "./styled";
import type { SortableItemData, SortableItemProps } from "./types";

export const SortableItem = <T extends SortableItemData>({
  item,
  children,
  className,
  disabled = false,
  dragHandleSelector,
}: SortableItemProps<T>): JSX.Element => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver,
    setActivatorNodeRef,
  } = useSortable({
    id: item.id,
    disabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleRef = (node: HTMLDivElement | null) => {
    setNodeRef(node);

    if (node && dragHandleSelector) {
      const handle = node.querySelector(dragHandleSelector);
      if (handle) {
        setActivatorNodeRef(handle as HTMLElement);
      }
    }
  };

  const dragListeners = dragHandleSelector ? {} : listeners;
  const dragAttributes = dragHandleSelector ? {} : attributes;

  React.useEffect(() => {
    if (dragHandleSelector && setActivatorNodeRef) {
      const element = document.querySelector(
        `#sortable-item-${item.id} ${dragHandleSelector}`,
      );
      if (element) {
        setActivatorNodeRef(element as HTMLElement);
      }
    }
  }, [dragHandleSelector, item.id, setActivatorNodeRef]);

  return (
    <SortableItemWrapper
      ref={handleRef}
      id={`sortable-item-${item.id}`}
      className={className}
      $isDragging={isDragging}
      $isOver={isOver}
      $transform={style.transform}
      $transition={style.transition}
      disabled={disabled}
      role="listitem"
      aria-grabbed={isDragging}
      aria-dropeffect={disabled ? "none" : "move"}
      tabIndex={disabled ? -1 : 0}
      {...dragAttributes}
      {...dragListeners}
    >
      {children}
    </SortableItemWrapper>
  );
};
